import CreatePageConstants, {
    DEFAULT_FLOCK_SETTINGS,
    DEFAULT_IMAGE_ELEMENT_SIZE_FACTOR,
    ELEMENTS_PANEL_TABS,
    MAX_IMAGE_ELEMENTS,
    MEDIA_RESOURCE_TYPE,
    MEDIA_SOURCE_TYPE,
    PARTICLE_SHAPE_TYPES
} from '../../CreatePageConstants';
import { CANVAS_ELEMENT_SNAPPING, GLOW_TYPE, MOVEMENT_DIRECTION, PARTICLE_DIRECTIONS, VISUALIZER_SHAPE } from '../../Preview/PreviewDisplayConstants';
import { calculateFittedSize, getFrameBaseSize } from '../VideoElementsEditor/ImageElements/imageElementSizeCalculations';
import { getEmojiImagePath } from '../../../../../utilities/emojiUtils';
import { randomString } from '../../../../../utilities/randomUtilities';
import { createActions } from '../../store';

const { SETTINGS_PANELS, VISUALIZER_SUB_TABS } = CreatePageConstants;

const GHOST_EMOJI_UNICODE = '1f47b';
const DEFAULT_PARTICLE_TINT_COLOR = '0xfbfbfb';

// Matches DEFAULT_VIDEO_SETTINGS.visualizer.logoGlowSettings so enabling the showcase
// always lands on the default glow look, even for documents with older values. glowType has to
// be spelled out: the reducer merges into the stored settings, and presets and saved documents
// carry "outer" from the backend, so without it the showcase kept Outer Glow.
const DEFAULT_LOGO_GLOW_SHOWCASE_SETTINGS = { enabled: true, glowType: GLOW_TYPE.INNER, blur: 3.6, scale: 1 };

const readParticles = (videoSettings) => videoSettings.elements.particles;

const isCircleVisualizer = (videoSettings) => videoSettings.visualizer?.shape === VISUALIZER_SHAPE.CIRCLE;

// "Remove Logo" in the logo picker only flips logoVisible off (the media source stays behind so the
// logo can be brought back), so a visible flag plus a media source is what "a logo is applied" means.
const hasVisibleLogo = (videoSettings) =>
    Boolean(videoSettings.visualizer?.logoVisible) && Boolean(videoSettings.visualizer?.mediaSource);

// Same rule as Preview/helpers.js isColorOrGradient; duplicated here because that module pulls in
// every PixiJS element class, which the registry has no business loading.
const hasColorOrGradientBackdrop = (videoSettings) => {
    const mediaResourceType = videoSettings.background?.mediaSource?.mediaResourceType;
    return mediaResourceType === MEDIA_RESOURCE_TYPE.COLOR || mediaResourceType === MEDIA_RESOURCE_TYPE.GRADIENT;
};

const CIRCLE_ONLY_DISABLED_TOOLTIP = 'Only available for Circle visualizers.';

// Wave layers render back-to-front by array order (index 0 first), so the front/topmost
// layer — "Wave Layer 1" in WaveLayersEditor's reversed list — is always the last index.
const getFrontWaveLayerIndex = (videoSettings) => {
    const waveCircles = videoSettings.visualizer?.waveCircles;
    return waveCircles && waveCircles.length > 0 ? waveCircles.length - 1 : 0;
};

const enableParticlesIfMissing = (dispatch, videoSettings) => {
    if (readParticles(videoSettings).items.length === 0)
        dispatch(createActions.addParticleElement());
};

// Captured when Flock Particle Movement is enabled from this panel and consumed when it is
// disabled again, so the showcase neither leaves particles switched on for a user who had them
// off nor resets a hand-picked direction (Up, Left, ...) to Out. Transient panel state, kept
// outside Redux for the same reasons as particleSettingsBeforeRotationShowcase below.
let particleSettingsBeforeFlockShowcase = null;

const captureParticleSettingsBeforeFlockShowcase = (videoSettings) => {
    const particles = readParticles(videoSettings);

    particleSettingsBeforeFlockShowcase = {
        enabled: Boolean(particles.enabled),
        direction: particles.direction ?? PARTICLE_DIRECTIONS.OUT,
        flock: particles.flock ? { ...particles.flock } : null
    };
};

// A snapshot can only hold direction FLOCK when particles were switched off (otherwise the feature
// would already have been active and could not be enabled). Restoring FLOCK together with
// enabled: true would leave the showcase switch checked, so that combination falls back to Out.
const getDirectionToRestoreAfterFlockShowcase = (snapshot) =>
    snapshot.direction === PARTICLE_DIRECTIONS.FLOCK && snapshot.enabled
        ? PARTICLE_DIRECTIONS.OUT
        : snapshot.direction;

const restoreParticleSettingsAfterFlockShowcase = (dispatch, snapshot) => {
    dispatch(createActions.updateElementsSettings({
        particles: {
            enabled: snapshot.enabled,
            direction: getDirectionToRestoreAfterFlockShowcase(snapshot),
            ...(snapshot.flock ? { flock: snapshot.flock } : {})
        }
    }));
};

// Matches the particle item that addParticleElement creates, so falling back to it after the
// Particle Rotation showcase lands on the same look as a freshly added particle.
const DEFAULT_PARTICLE_LOOK = {
    shape: PARTICLE_SHAPE_TYPES.CIRCLE,
    mediaData: null,
    color: DEFAULT_PARTICLE_TINT_COLOR,
    rotationEnabled: false
};

// Captured when Particle Rotation is enabled from this panel and consumed when it is disabled
// again, so the showcase ghost emoji does not permanently replace the user's own particle.
// Kept outside Redux on purpose: it is transient panel state that must not be saved, exported
// or undone together with the video settings. It does not survive a page reload — see
// revertRotationShowcaseWithoutSnapshot for that case.
let particleSettingsBeforeRotationShowcase = null;

const isShowcaseGhostParticle = (particleItem) =>
    particleItem?.shape === PARTICLE_SHAPE_TYPES.EMOJI
    && particleItem.mediaData === getEmojiImagePath(GHOST_EMOJI_UNICODE);

const captureParticleSettingsBeforeRotationShowcase = (videoSettings) => {
    const particles = readParticles(videoSettings);
    const firstParticle = particles.items[0];

    particleSettingsBeforeRotationShowcase = {
        enabled: Boolean(particles.enabled),
        item: firstParticle
            ? {
                shape: firstParticle.shape,
                mediaData: firstParticle.mediaData,
                color: firstParticle.color,
                rotationEnabled: Boolean(firstParticle.rotationEnabled)
            }
            : null
    };
};

const restoreParticleSettingsFromSnapshot = (dispatch, snapshot) => {
    // A null item means there was no particle before the showcase and enableParticlesIfMissing
    // created the default one, so the default look is the closest thing to "what was there".
    dispatch(createActions.updateParticleElement(0, snapshot.item ?? DEFAULT_PARTICLE_LOOK));
    dispatch(createActions.updateElementsSettings({ particles: { enabled: snapshot.enabled } }));
};

// Without a snapshot (page reloaded, or rotation was switched on from the Elements panel) only
// what the showcase itself is known to have applied can be undone: the ghost emoji goes back to
// the default circle, while any other user-picked shape is left untouched.
const revertRotationShowcaseWithoutSnapshot = (dispatch, videoSettings) => {
    const firstParticle = readParticles(videoSettings).items[0];
    const revertedProperties = isShowcaseGhostParticle(firstParticle)
        ? DEFAULT_PARTICLE_LOOK
        : { rotationEnabled: false };

    dispatch(createActions.updateParticleElement(0, revertedProperties));
};

// The picture the Image Elements showcase puts on the canvas: the red Specterr.com watermark.
const SHOWCASE_WATERMARK_IMAGE_URL = '/images/specterr-watermark.png';

// The watermark artwork's own pixel size. Every picture is fitted into IMAGE_ELEMENT_CONFIG.BOX_SIZE
// by its own proportions, so how much of the frame this one covers — and with it the position that
// puts its edges on the safe zone lines — cannot be worked out without them. They are read off the
// file rather than the texture because the texture is still downloading when the switch is flipped.
const SHOWCASE_WATERMARK_NATURAL_SIZE = { width: 604, height: 160 };

// Video settings saved before image elements existed have neither list, and the two keys are kept in
// step by the reducer, so the first one that is there is the list.
const readImageElements = (videoSettings) =>
    videoSettings.elements.imageElements || videoSettings.elements.images || [];

const isShowcaseImageElement = (imageElement) =>
    imageElement?.mediaSource?.data?.url === SHOWCASE_WATERMARK_IMAGE_URL;

// The same shape CreatePage/helpers.js createMediaSource builds, written out here rather than
// imported: that module reaches axios through fileUtilities, and this registry is a list of plain
// descriptors that the whole editor loads. It is kept free of anything heavier than constants for
// the same reason it does not import Preview/helpers.js.
const createShowcaseImageMediaSource = () => ({
    operationStamp: randomString(),
    data: { url: SHOWCASE_WATERMARK_IMAGE_URL, originalImageUrl: null },
    mediaSourceType: MEDIA_SOURCE_TYPE.SPECTERR_CDN,
    mediaId: null,
    mediaResourceType: MEDIA_RESOURCE_TYPE.IMAGE
});

// Where the picture's middle has to sit for its left and top edges to land on the upper-left safe
// zone lines — the very lines a dragged picture snaps onto, so the showcase parks the watermark
// where a user would park it by hand. The frame is derived from the aspect ratio rather than taken
// from the landscape one, because a portrait frame is only 281 units wide and a position measured
// against 888 would carry the picture off the canvas. A position is a whole slider step, hence the
// rounding.
const calculateUpperLeftImagePosition = (aspectRatio) => {
    const frameSize = getFrameBaseSize(aspectRatio);
    const fittedSize = calculateFittedSize(SHOWCASE_WATERMARK_NATURAL_SIZE);
    const margin = CANVAS_ELEMENT_SNAPPING.SAFE_ZONE_MARGIN;

    return {
        x: Math.round(-frameSize.width / 2 + margin + fittedSize.width * DEFAULT_IMAGE_ELEMENT_SIZE_FACTOR / 2),
        y: Math.round(-frameSize.height / 2 + margin + fittedSize.height * DEFAULT_IMAGE_ELEMENT_SIZE_FACTOR / 2)
    };
};

// The pictures already on the canvas when Image Elements was enabled from this panel, consumed when
// it is disabled again. The reducer is what builds the element and gives it an id, so the showcase
// cannot note that id down as it adds the picture; remembering what was there instead is enough to
// tell its own watermark apart from one the user had placed themselves. Transient panel state, kept
// outside Redux for the same reasons as the particle snapshots above, and like them it does not
// survive a page reload — see findShowcaseImageElementIndex for that case.
let imageElementIdsBeforeShowcase = null;

const findLastIndexOf = (items, predicate) => {
    for (let index = items.length - 1; index >= 0; index--) {
        if (predicate(items[index]))
            return index;
    }

    return -1;
};

// The watermark this panel put on the canvas: the one carrying the showcase picture that was not
// there before. Without a snapshot (page reloaded, or the same picture was added from the Elements
// panel) the last watermark is the closest guess, and it is the right one whenever the showcase is
// what added it.
const findShowcaseImageElementIndex = (imageElements) => {
    if (!imageElementIdsBeforeShowcase)
        return findLastIndexOf(imageElements, isShowcaseImageElement);

    const addedByShowcase = imageElements.findIndex((imageElement) =>
        isShowcaseImageElement(imageElement) && !imageElementIdsBeforeShowcase.includes(imageElement.id));

    return addedByShowcase === -1
        ? findLastIndexOf(imageElements, isShowcaseImageElement)
        : addedByShowcase;
};

/**
 * Showcase feature descriptors rendered by the Features panel. Adding a feature in a
 * future release only requires appending a descriptor here — the panel UI, badges,
 * undo batching and analytics all derive from this registry.
 *
 * Descriptor contract:
 *   id         - stable identifier used for badge persistence and analytics
 *   title      - display label
 *   tooltip    - optional short explanation shown by the help button; omitted hides the button
 *   isNew      - whether the feature shows a NEW badge until first enabled
 *   isActive   - (videoSettings, editor) => boolean, drives the switch state
 *   isDisabled - optional (videoSettings, editor) => boolean; true greys the row out and locks the
 *                switch because the effect cannot show with the current settings
 *   disabledTooltip - optional text shown over the locked switch explaining why
 *   isAvailable - optional (videoSettings, editor) => boolean; false leaves the row out entirely
 *   onEnable   - (dispatch, videoSettings, editor) applies the showcase configuration
 *   onDisable  - (dispatch, videoSettings, editor) reverts the feature
 *   navigation - optional { panel, visualizerTab?, elementsTab? } deep-link target of the arrow
 *                button; omitted hides the button
 *   editorSetting - true when the feature is an editor preference rather than a video setting, so
 *                   the toggle is kept out of the undo history
 */
const featuresRegistry = [
    {
        id: 'fullscreen_preview',
        title: 'Full Screen Preview',
        isNew: true,
        editorSetting: true,
        isAvailable: (videoSettings, editor) => editor.fullscreenSupported,
        isActive: (videoSettings, editor) => editor.previewFullscreen,
        onEnable: (dispatch, videoSettings, editor) => editor.togglePreviewFullscreen(),
        onDisable: (dispatch, videoSettings, editor) => editor.togglePreviewFullscreen()
    },
    {
        id: 'optimization_mode',
        title: 'Optimization Mode',
        tooltip: 'Optimization Mode reduces memory and CPU usage by limiting some editor features. Exports are not affected.',
        isNew: true,
        editorSetting: true,
        isActive: (videoSettings, editor) => editor.optimizationModeEnabled,
        onEnable: (dispatch, videoSettings, editor) => editor.setOptimizationMode(true),
        onDisable: (dispatch, videoSettings, editor) => editor.setOptimizationMode(false)
    },
    {
        id: 'inward_visualizer',
        title: 'Inward Visualizer',
        tooltip: 'For circle visualizers: waves extend inward toward the center instead of outward.',
        isNew: true,
        isActive: (videoSettings) => {
            const waveCircles = videoSettings.visualizer?.waveCircles;
            if (!waveCircles || waveCircles.length === 0)
                return false;

            const frontWaveLayer = waveCircles[getFrontWaveLayerIndex(videoSettings)];
            const frontWaveLayerIsInward = frontWaveLayer?.customOptions?.enabled
                ? frontWaveLayer.customOptions.movementDirection === MOVEMENT_DIRECTION.INWARD
                : videoSettings.visualizer.movementDirection === MOVEMENT_DIRECTION.INWARD;

            return frontWaveLayerIsInward && !videoSettings.visualizer.logoVisible;
        },
        isDisabled: (videoSettings) => !isCircleVisualizer(videoSettings),
        disabledTooltip: CIRCLE_ONLY_DISABLED_TOOLTIP,
        onEnable: (dispatch, videoSettings) => {
            const waveCircles = videoSettings.visualizer?.waveCircles;
            if (!waveCircles || waveCircles.length === 0)
                return;

            const frontWaveLayerIndex = getFrontWaveLayerIndex(videoSettings);
            const frontWaveLayer = waveCircles[frontWaveLayerIndex];

            dispatch(createActions.updateVisualizerSettings({ logoVisible: false }));

            // Customize must be turned on before a per-layer direction can take effect —
            // otherwise the renderer keeps following the global (all-layers) direction.
            if (!frontWaveLayer?.customOptions?.enabled)
                dispatch(createActions.updateWaveLayerCustomOptions(frontWaveLayerIndex, 'enabled', true));

            dispatch(createActions.updateWaveLayerCustomOptions(frontWaveLayerIndex, 'movementDirection', MOVEMENT_DIRECTION.INWARD));
        },
        onDisable: (dispatch, videoSettings) => {
            const waveCircles = videoSettings.visualizer?.waveCircles;
            if (!waveCircles || waveCircles.length === 0)
                return;

            // Enabling hides the logo, so disabling must bring it back — otherwise the
            // logo silently stays hidden and later logo features (e.g. Logo Glow) look broken.
            dispatch(createActions.updateVisualizerSettings({ logoVisible: true }));

            const frontWaveLayerIndex = getFrontWaveLayerIndex(videoSettings);
            dispatch(createActions.updateWaveLayerCustomOptions(frontWaveLayerIndex, 'movementDirection', MOVEMENT_DIRECTION.OUTWARD));

            // Turn Customize back off so the front layer inherits global settings again
            // (e.g. Hollow Visualizer) instead of staying pinned to its own customOptions.
            dispatch(createActions.updateWaveLayerCustomOptions(frontWaveLayerIndex, 'enabled', false));
        },
        navigation: { panel: SETTINGS_PANELS.VISUALIZER, visualizerTab: VISUALIZER_SUB_TABS.SHAPE }
    },
    {
        id: 'image_elements',
        title: 'Image Elements',
        tooltip: 'Add logos, album covers, and more to your video. Drag and drop to reposition your images.',
        isNew: true,
        isActive: (videoSettings) => readImageElements(videoSettings).some(isShowcaseImageElement),
        // A canvas already carrying the most pictures it is allowed has no room for the showcase
        // one. It stays unlocked while the showcase picture is one of them, or its own switch
        // could never be turned back off.
        isDisabled: (videoSettings) => {
            const imageElements = readImageElements(videoSettings);
            return imageElements.length >= MAX_IMAGE_ELEMENTS && !imageElements.some(isShowcaseImageElement);
        },
        disabledTooltip: `Maximum of ${MAX_IMAGE_ELEMENTS} image elements reached.`,
        onEnable: (dispatch, videoSettings) => {
            const imageElements = readImageElements(videoSettings);
            imageElementIdsBeforeShowcase = imageElements.map((imageElement) => imageElement.id);

            const position = calculateUpperLeftImagePosition(videoSettings.aspectRatio);

            // Passing position into addImageElement ensures the element is born directly in the corner
            // rather than defaulting to (0, 0) and flashing or staying centered.
            dispatch(createActions.addImageElement(createShowcaseImageMediaSource(), { position }));
            dispatch(createActions.updateImageElement(imageElements.length, { position }));
        },
        onDisable: (dispatch, videoSettings) => {
            const showcaseImageElementIndex = findShowcaseImageElementIndex(readImageElements(videoSettings));
            imageElementIdsBeforeShowcase = null;

            // Only the picture this panel added is taken away. Restoring a whole snapshot of the
            // list instead would throw away every other picture the user placed, moved or resized
            // while the showcase was on.
            if (showcaseImageElementIndex !== -1)
                dispatch(createActions.removeImageElement(showcaseImageElementIndex));
        },
        navigation: { panel: SETTINGS_PANELS.ELEMENTS, elementsTab: ELEMENTS_PANEL_TABS.IMAGE_ELEMENTS }
    },
    {
        id: 'flock_movement',
        title: 'Flock Particle Movement',
        tooltip: 'Particles move around like a flock of birds.',
        isNew: true,
        isActive: (videoSettings) => {
            const particles = readParticles(videoSettings);
            return Boolean(particles.enabled) && particles.direction === PARTICLE_DIRECTIONS.FLOCK;
        },
        onEnable: (dispatch, videoSettings) => {
            captureParticleSettingsBeforeFlockShowcase(videoSettings);
            enableParticlesIfMissing(dispatch, videoSettings);
            dispatch(createActions.updateElementsSettings({
                particles: {
                    enabled: true,
                    direction: PARTICLE_DIRECTIONS.FLOCK,
                    flock: { customMode: false, ...DEFAULT_FLOCK_SETTINGS }
                }
            }));
        },
        onDisable: (dispatch) => {
            const snapshot = particleSettingsBeforeFlockShowcase;
            particleSettingsBeforeFlockShowcase = null;

            if (snapshot) {
                restoreParticleSettingsAfterFlockShowcase(dispatch, snapshot);
                return;
            }

            // No snapshot (page reloaded, or Flock was picked from the Elements panel): the
            // previous direction is unknown, so fall back to the default one.
            dispatch(createActions.updateElementsSettings({
                particles: { direction: PARTICLE_DIRECTIONS.OUT }
            }));
        },
        navigation: { panel: SETTINGS_PANELS.ELEMENTS, elementsTab: ELEMENTS_PANEL_TABS.OTHER }
    },
    {
        id: 'transparent_wave',
        title: 'Transparent Wave',
        tooltip: 'Wave layers create a transparent area in the visualizer. This works well with Inward Visualizer.',
        isNew: true,
        isActive: (videoSettings) => {
            const waveCircles = videoSettings.visualizer?.waveCircles;
            if (!waveCircles || waveCircles.length === 0)
                return false;

            const frontWaveLayer = waveCircles[getFrontWaveLayerIndex(videoSettings)];
            return Boolean(frontWaveLayer?.customOptions?.cutout);
        },
        onEnable: (dispatch, videoSettings) => {
            if (videoSettings.visualizer.waveCircles.length === 0)
                return;

            dispatch(createActions.updateWaveLayerCustomOptions(getFrontWaveLayerIndex(videoSettings), 'cutout', true));
        },
        onDisable: (dispatch, videoSettings) => {
            if (videoSettings.visualizer.waveCircles.length === 0)
                return;

            dispatch(createActions.updateWaveLayerCustomOptions(getFrontWaveLayerIndex(videoSettings), 'cutout', false));
        },
        navigation: { panel: SETTINGS_PANELS.VISUALIZER, visualizerTab: VISUALIZER_SUB_TABS.LAYERS }
    },
    {
        id: 'hollow_visualizer',
        title: 'Hollow Visualizer',
        tooltip: 'For circle visualizers: at rest, the visualizer is completely transparent. Waves extend from this transparent circle.',
        isNew: true,
        isActive: (videoSettings) => videoSettings.visualizer.hollowCenter === true,
        isDisabled: (videoSettings) => !isCircleVisualizer(videoSettings),
        disabledTooltip: CIRCLE_ONLY_DISABLED_TOOLTIP,
        onEnable: (dispatch) => {
            dispatch(createActions.updateVisualizerSettings({ hollowCenter: true }));
        },
        onDisable: (dispatch) => {
            dispatch(createActions.updateVisualizerSettings({ hollowCenter: false }));
        },
        navigation: { panel: SETTINGS_PANELS.VISUALIZER, visualizerTab: VISUALIZER_SUB_TABS.SHAPE }
    },
    {
        id: 'particle_rotation',
        title: 'Particle Rotation',
        tooltip: 'Particles gently rotate as they move.',
        isNew: true,
        isActive: (videoSettings) => {
            const particles = readParticles(videoSettings);
            return Boolean(particles.enabled) && Boolean(particles.items[0]?.rotationEnabled);
        },
        onEnable: (dispatch, videoSettings) => {
            captureParticleSettingsBeforeRotationShowcase(videoSettings);
            enableParticlesIfMissing(dispatch, videoSettings);
            dispatch(createActions.updateElementsSettings({ particles: { enabled: true } }));
            dispatch(createActions.updateParticleElement(0, {
                shape: PARTICLE_SHAPE_TYPES.EMOJI,
                mediaData: getEmojiImagePath(GHOST_EMOJI_UNICODE),
                color: DEFAULT_PARTICLE_TINT_COLOR,
                rotationEnabled: true
            }));
        },
        onDisable: (dispatch, videoSettings) => {
            if (readParticles(videoSettings).items.length === 0)
                return;

            const snapshot = particleSettingsBeforeRotationShowcase;
            particleSettingsBeforeRotationShowcase = null;

            if (snapshot)
                restoreParticleSettingsFromSnapshot(dispatch, snapshot);
            else
                revertRotationShowcaseWithoutSnapshot(dispatch, videoSettings);
        },
        navigation: { panel: SETTINGS_PANELS.ELEMENTS, elementsTab: ELEMENTS_PANEL_TABS.OTHER }
    },
    {
        id: 'logo_glow',
        title: 'Logo Glow',
        tooltip: 'Applies glow effects to the visualizer logo.',
        isNew: true,
        isActive: (videoSettings) => Boolean(videoSettings.visualizer.logoGlowSettings?.enabled),
        isDisabled: (videoSettings) => !isCircleVisualizer(videoSettings) || !hasVisibleLogo(videoSettings),
        disabledTooltip: 'Requires a Circle visualizer with a logo.',
        onEnable: (dispatch) => {
            dispatch(createActions.updateVisualizerSettings({
                logoGlowSettings: DEFAULT_LOGO_GLOW_SHOWCASE_SETTINGS
            }));
        },
        onDisable: (dispatch) => {
            dispatch(createActions.updateVisualizerSettings({ logoGlowSettings: { enabled: false } }));
        },
        navigation: { panel: SETTINGS_PANELS.VISUALIZER, visualizerTab: VISUALIZER_SUB_TABS.EFFECTS }
    },
    {
        id: 'backdrop_zoom',
        title: 'Backdrop Zoom',
        tooltip: 'The backdrop gently zooms in and out.',
        isNew: true,
        isActive: (videoSettings) => Boolean(videoSettings.background.slowZoom?.enabled),
        isDisabled: hasColorOrGradientBackdrop,
        disabledTooltip: 'Only available for image or video backdrops.',
        onEnable: (dispatch) => {
            // Mirrors selecting Zoom in the Backdrop Movement control: the movement
            // modes are mutually exclusive, and motion blur belongs to Rumble only.
            dispatch(createActions.updateBackgroundSettings({
                drift: { enabled: false },
                slowZoom: { enabled: true },
                shakeEnabled: false,
                motionBlurEnabled: false
            }));
        },
        onDisable: (dispatch) => {
            dispatch(createActions.updateBackgroundSettings({ slowZoom: { enabled: false } }));
        },
        navigation: { panel: SETTINGS_PANELS.BACKDROP }
    }
];

export default featuresRegistry;
